// The protocol's own vocabulary, as types.
//
// These mirror pulsemesh-threads.md exactly. They are deliberately kept
// apart from the honesty model in `honesty.ts`: this file is what the
// wire says, that one is what a screen is allowed to claim, and the gap
// between the two is where every dishonest UI in this product category
// lives.

/** §5.3 — what the vehicle is doing at an instant. */
export const THREAD_STATE = {
  SCHEDULED: 1,
  EN_ROUTE: 2,
  DWELLING: 3,
  COMPLETED: 4,
  CANCELED: 5,
  OFF_PLAN: 6
} as const;
export type ThreadState = (typeof THREAD_STATE)[keyof typeof THREAD_STATE];

/** §11 — what a leaked capability is worth. Not a bandwidth setting. */
export const THREAD_MODE = { COARSE: 1, FINE: 2 } as const;
export type ThreadMode = (typeof THREAD_MODE)[keyof typeof THREAD_MODE];

/** §5.2 field 5 — how the vehicle moves. */
export const TRAVEL_MODE = { UNSPECIFIED: 0, CAR: 1, BIKE: 2, FOOT: 3 } as const;
export type TravelModeCode = (typeof TRAVEL_MODE)[keyof typeof TRAVEL_MODE];
export type TravelModeName = "car" | "bike" | "foot";

/** §5.2.1 — an outcome is asserted, never inferred. 0 means nobody has said. */
export const STOP_OUTCOME = { PENDING: 0, DELIVERED: 1, SKIPPED: 2, FAILED: 3 } as const;
export type StopOutcomeCode = (typeof STOP_OUTCOME)[keyof typeof STOP_OUTCOME];
export type StopOutcomeName = "pending" | "delivered" | "skipped" | "failed";

/** §5.2.1 — a machine-readable why, so each app says it in its own words. */
export const STOP_REASON = {
  NONE: 0,
  ABSENT: 1,
  REFUSED: 2,
  INACCESSIBLE: 3,
  PARCEL_MISSING: 4,
  OTHER: 5
} as const;
export type StopReasonCode = (typeof STOP_REASON)[keyof typeof STOP_REASON];

export const OUTCOME_NAME: Record<number, StopOutcomeName> = {
  0: "pending",
  1: "delivered",
  2: "skipped",
  3: "failed"
};

export const TRAVEL_MODE_NAME: Record<number, TravelModeName | null> = {
  0: null,
  1: "car",
  2: "bike",
  3: "foot"
};

/** §5.2 — one decrypted PMTP body, as a subscriber hands it over. */
export interface ThreadUpdate {
  unixSeconds: number;
  state: number;
  mode: number;
  travelMode: number;
  /** Null when the position was withheld (§11 coarse, or a suppressed leg). */
  segment: string | null;
  ratio: number;
  leafCell: number;
  speedBin: number;
  /** The last stop the run has *dealt with*, in plan order. Never a high-water mark. */
  stopIndex: number;
  /** Cumulative in every record, so the newest update carries the whole day. */
  outcomes: number[];
  lastOutcome: {
    stopIndex: number;
    outcome: number;
    reasonCode: number;
    /**
     * Whether a proof exists, signed. Which proof is the accumulator's
     * job — `photoChain` below — and the wire deliberately sends one
     * boolean here: "there is a photo for this stop" is the claim a card
     * makes first, and it needs no fetch. An earlier shape carried a
     * `photoHash` on this object; nothing has, for a while, and reading
     * it was how a driver's photo showed nothing at all.
     */
    hasPhoto: boolean;
  } | null;
  /**
   * §20.7.1 — the head of the run's photo chain, lowercase hex, or null
   * while the run has taken none. A commitment over every commitment the
   * run has published, re-stated in each record and signed with it.
   */
  photoChain: string | null;
  note: string | null;
  seq: number;
  receivedAt: number;
}

/** §12 — the four rows, verbatim from `subscriber.status()`. */
export type DegradationRow = "thread+traffic" | "thread-only" | "traffic-only" | "neither";

export interface SubscriberStatus {
  row: DegradationRow;
  live: boolean;
  hasPosition: boolean;
  ageSeconds: number | null;
  state: number;
  stopIndex: number;
  outcomes: number[];
  lastOutcome: ThreadUpdate["lastOutcome"];
  travelMode: number;
  claim: string;
}

/** §9 — what `estimateArrival` returns, before this package interprets it. */
export interface RawArrival {
  arrivalMillis: number | null;
  secondsFromObservation?: number | null;
  secondsFromNow?: number | null;
  stopsAway?: number | null;
  outcome?: number;
  outcomeName?: string;
  reasonCode?: number | null;
  basis: "live-traffic" | "static-metric" | "marked" | "schedule";
  positionBasis: "reported-position" | "last-stop" | "none";
  observationAgeSeconds?: number;
  stale?: boolean;
  profile?: string | null;
  profileBasis?: "matched" | "mismatched" | "unstated";
  travelModeName?: TravelModeName | null;
  travelModeMismatch?: boolean;
}

/**
 * §5.4 — the 78-byte capability, decoded. The engine's own shape: the
 * capability secret travels beside the verification key, so neither can
 * stand in for the other after compromise. Core treats it as opaque —
 * decoded by the engine, handed back to the engine.
 */
export interface ThreadLink {
  version: number;
  threadSecret: Uint8Array;
  rootPublicKey: Uint8Array;
  epochPrefix8: Uint8Array;
  notAfter: number;
  delegated: boolean;
}

/** A run plan, as `estimateArrival` and the publisher want it. */
export interface RunPlanStop {
  index: number;
  lat: number;
  lon: number;
  label?: string | null;
  plannedUnixSeconds?: number | null;
}

export interface RunPlan {
  stops: RunPlanStop[];
  dwellSeconds?: number;
  planRef?: Uint8Array;
  travelMode?: number;
}
